import { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  X,
  CheckCircle,
  Leaf,
  FlaskConical,
  Pill,
  Package,
  CalendarClock,
  Clock,
  Lightbulb,
  AlertCircle,
  UtensilsCrossed,
  Activity,
  BookOpen,
  Info,
  AlertTriangle,
  Utensils,
  Scale,
} from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import type { ContentBlock, ContentBlockId } from "@src/packages/content";
import { getBlock, InteractionsList } from "@src/packages/content";
import {
  Dialog,
  DialogBodyText,
  DialogHeader,
  MoonDetailRow,
  useScrollHaptics,
  springs,
  IconButton,
} from "@louise/moon-ui";
import {
  useLocale,
  useTranslations,
  type TranslationFunction,
} from "@src/packages/i18n";
import InterestButton from "./InterestButton";
import DetailSectionTitle from "./DetailSectionTitle";
import EvidenceLevelModal from "./EvidenceLevelModal";
import EvidenceSourceRows from "./EvidenceSourceRows";
import SupplementOrderingNote from "./SupplementOrderingNote";
import FactSheetContextCard, {
  type FactSheetContextRow,
} from "./FactSheetContextCard";
import FactSheetHeader, { type FactSheetTagItem } from "./FactSheetHeader";
import type { MedicationUserContext } from "./MedicationFactsheetSheet";
import LinkedContentSheet from "./LinkedContentSheet";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { useMedicationLogs } from "@src/packages/care/hooks/useMedicationLogs";
import type { MedicationLog } from "@src/packages/care/hooks/useMedicationLogs";
import { useMedications } from "@src/packages/care/hooks/useMedications";
import type { Medication } from "@src/packages/care/hooks/useMedications";
import { EMBER } from "../../onboarding/theme";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors } from "@louise/theme/tokens";
const SUPPLEMENT_BOTTLES = [
  require("../../assets/images/supplement-bottle.png"),
  require("../../assets/images/supplement-bottle-2.png"),
  require("../../assets/images/supplement-bottle-3.png"),
];

const FOOD_ICONS: Record<string, string> = {
  "Organ meats": "🥩",
  Sardines: "🐟",
  Broccoli: "🥦",
  Cauliflower: "🌿",
  "Fatty fish": "🐟",
  "Egg yolks": "🥚",
  "Fortified milk": "🥛",
  Mushrooms: "🍄",
  Salmon: "🐟",
  Walnuts: "🥜",
  Flaxseeds: "🌱",
  "Leafy greens": "🥬",
  Legumes: "🫘",
  "Citrus fruits": "🍊",
  "Fortified grains": "🌾",
  "Fortified cereals": "🌾",
  Chicken: "🍗",
  Turkey: "🍗",
  Garlic: "🧄",
  Onions: "🧅",
  "Cruciferous vegetables": "🥦",
  Oysters: "🦪",
  "Red meat": "🥩",
  Fish: "🐟",
  Poultry: "🍗",
  "Pumpkin seeds": "🎃",
  Chickpeas: "🫘",
  Milk: "🥛",
  Avocado: "🥑",
  "Brazil nuts": "🥜",
  Tuna: "🐟",
  Eggs: "🥚",
  "Sunflower seeds": "🌻",
  Beans: "🫘",
  "Brown rice": "🍚",
  Cantaloupe: "🍈",
  "Bell peppers": "🫑",
  Strawberries: "🍓",
  Kiwi: "🥝",
  Tomatoes: "🍅",
  Almonds: "🥜",
  Spinach: "🥬",
  "Tart cherries": "🍒",
  Pistachios: "🥜",
  "Goji berries": "🫐",
  Lentils: "🫘",
  "Dark chocolate": "🍫",
  "Red grapes": "🍇",
  Blueberries: "🫐",
  Peanuts: "🥜",
  "Turmeric root": "🫚",
  "Curry powder": "🟡",
  Yogurt: "🥛",
  Kefir: "🥛",
  Sauerkraut: "🥬",
  Kimchi: "🥬",
};

/**
 * Convert an English food name (the key used in content blocks, e.g. "Organ meats")
 * into the kebab_case i18n key (e.g. "organ_meats").
 */
function toFoodKey(name: string): string {
  return name.toLowerCase().replace(/\s+/g, "_");
}

/** Translated display name for a food (used in chips and the modal title). */
function getFoodLabel(t: TranslationFunction, name: string): string {
  const key = toFoodKey(name);
  const fallback = `access.supplements.food_info.label.${key}`;
  const translated = t(fallback);
  return translated === fallback ? name : translated;
}

/**
 * Translated serving / amount / note for a food, or null when the food is unknown
 * (e.g. a content block references a food we don't have data for).
 */
function getFoodInfo(
  t: TranslationFunction,
  name: string,
): { serving: string; amount: string; note: string } | null {
  const key = toFoodKey(name);
  const servingPath = `access.supplements.food_info.serving.${key}`;
  const amountPath = `access.supplements.food_info.amount.${key}`;
  const notePath = `access.supplements.food_info.note.${key}`;
  const serving = t(servingPath);
  const amount = t(amountPath);
  const note = t(notePath);
  if (serving === servingPath || amount === amountPath || note === notePath) {
    return null;
  }
  return { serving, amount, note };
}

function FoodDetailModal({
  food,
  visible,
  onClose,
}: {
  food: string | null;
  visible: boolean;
  onClose: () => void;
}) {
  const t = useTranslations();
  if (!food) return null;
  const info = getFoodInfo(t, food);
  if (!info) return null;

  const emoji = FOOD_ICONS[food] ?? "🍽";
  const label = getFoodLabel(t, food);

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      closeAccessibilityLabel={t("learn.article_actions.close_accessibility")}
      closeAction="close_food_detail"
    >
      <DialogHeader
        leading={<Text style={{ fontSize: 22, lineHeight: 25 }}>{emoji}</Text>}
        title={label}
      />
      <View
        style={{
          flexDirection: "row",
          gap: 12,
          marginTop: 16,
          width: "100%",
        }}
      >
        <MoonDetailRow
          icon={Utensils}
          label={t("learn.supplement_detail.food_modal_serving")}
          value={info.serving}
          style={{ flex: 1 }}
        />
        <MoonDetailRow
          icon={Scale}
          label={t("learn.supplement_detail.food_modal_per_serving")}
          value={info.amount}
          style={{ flex: 1 }}
        />
      </View>
      <DialogBodyText style={{ marginTop: 14 }}>{info.note}</DialogBodyText>
    </Dialog>
  );
}

/** Stable bottle index from supplement ID — must match SupplementsScreen */
function bottleIndex(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % SUPPLEMENT_BOTTLES.length;
}

type Props = {
  item: ContentBlock<"supplement"> | null;
  onClose: () => void;
  userContext?: MedicationUserContext;
};

type Bioavailability = NonNullable<
  ContentBlock<"supplement">["body"]["bioavailability"]
>;

type TranslationFn = (
  key: string,
  opts?: { params?: Record<string, string> },
) => string;

type SupplementFactIcon = typeof FlaskConical;

function SupplementFactRow({
  icon: Icon,
  label,
  value,
  tone = "neutral",
  flex,
}: {
  icon: SupplementFactIcon;
  label: string;
  value: string;
  tone?: "neutral" | "caution";
  flex?: number;
}) {
  const color =
    tone === "caution"
      ? semanticColors.status.warningText
      : EMBER.colors.textMid;
  return (
    <View
      style={{
        ...(flex ? { flex } : null),
        gap: 8,
        padding: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
        backgroundColor: semanticColors.surface.subtle,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <Icon size={14} color={color} strokeWidth={1.75} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "600",
            color: tone === "caution" ? color : EMBER.colors.textMid,
            flex: 1,
          }}
        >
          {label}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          color: EMBER.colors.textLight,
          lineHeight: 18,
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function SupplementExpectationRows({
  items,
  tone,
}: {
  items: string[];
  tone: "positive" | "caution";
}) {
  const color =
    tone === "positive"
      ? semanticColors.status.success
      : semanticColors.status.warningText;
  const Icon = tone === "positive" ? CheckCircle : AlertCircle;

  return (
    <View
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
        backgroundColor: semanticColors.surface.glass,
        overflow: "hidden",
      }}
    >
      {items.map((item, index) => (
        <View
          key={`${tone}-${index}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 11,
            borderTopWidth: index === 0 ? 0 : 1,
            borderTopColor: semanticColors.border.subtle,
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                tone === "positive"
                  ? semanticColors.status.successTint
                  : semanticColors.status.warningTint,
              marginTop: 1,
            }}
          >
            <Icon size={13} color={color} strokeWidth={2} />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function BioavailabilityRows({
  bioavailability,
  t,
}: {
  bioavailability: Bioavailability;
  t: TranslationFn;
}) {
  const rows: {
    icon: SupplementFactIcon;
    label: string;
    value: string;
    tone?: "neutral" | "caution";
  }[] = [
    bioavailability.form
      ? {
          icon: Package,
          label: t("learn.supplement_detail.bioavailability_label_form"),
          value: bioavailability.form,
        }
      : null,
    bioavailability.absorption
      ? {
          icon: UtensilsCrossed,
          label: t("learn.supplement_detail.bioavailability_label_absorption"),
          value: bioavailability.absorption,
        }
      : null,
    bioavailability.metabolism
      ? {
          icon: Activity,
          label: t("learn.supplement_detail.bioavailability_label_metabolism"),
          value: bioavailability.metabolism,
        }
      : null,
    ...(bioavailability.cautions?.map((value) => ({
      icon: AlertCircle,
      label: t("learn.supplement_detail.bioavailability_label_caution"),
      value,
      tone: "caution" as const,
    })) ?? []),
  ].filter(Boolean) as {
    icon: SupplementFactIcon;
    label: string;
    value: string;
    tone?: "neutral" | "caution";
  }[];

  return (
    <View style={{ gap: 10 }}>
      {rows.map((row, index) => (
        <SupplementFactRow
          key={`${row.label}-${index}`}
          icon={row.icon}
          label={row.label}
          value={row.value}
          tone={row.tone}
        />
      ))}
    </View>
  );
}

function getSupplementCycleNote(timing?: string): string | null {
  if (!timing) return null;

  const genericPatterns = [
    /^Start 3 months before planned (cycle|conception)$/i,
    /^Start at least 1 month before conception attempt$/i,
    /^Start at least 1 month before planned conception$/i,
    /^Start 1-2 months before planned cycle$/i,
    /^Start 2-3 months before planned cycle$/i,
    /^Start immediately - most people are deficient$/i,
    /^Start anytime - most people are deficient$/i,
  ];

  return genericPatterns.some((pattern) => pattern.test(timing))
    ? null
    : timing;
}

function formatSupplementSchedule(
  times: string[],
  t: (key: string, opts?: { params?: Record<string, string> }) => string,
): string {
  if (times.length === 0) return t("medication_factsheet.schedule_as_needed");
  const formatTime = (raw: string): string => {
    const hour = parseInt(raw.split(":")[0] ?? "0", 10);
    if (hour < 12) return t("medication_factsheet.schedule_morning");
    if (hour < 17) return t("medication_factsheet.schedule_afternoon");
    if (hour < 21) return t("medication_factsheet.schedule_evening");
    return t("medication_factsheet.schedule_night");
  };
  const labels = times.map((raw) => `${formatTime(raw)} (${raw})`);
  return t("medication_factsheet.schedule_daily_template", {
    params: {
      count: String(times.length),
      labels: labels.join(" & "),
    },
  });
}

function formatSupplementDateCompact(dateStr: string, locale: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function hasPastSupplementEndDate(endDate: string | null | undefined): boolean {
  if (!endDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(`${endDate}T00:00:00`);
  return end.getTime() < today.getTime();
}

function isSupplementStopped(notes: string | null | undefined): boolean {
  if (!notes) return false;
  return /^stopped(\s|:|$)/i.test(notes.trimStart());
}

function formatSupplementDuration(
  startDate: string,
  endDate: string | null | undefined,
  t: (key: string, opts?: { params?: Record<string, string> }) => string,
): string {
  if (!endDate) return t("medication_factsheet.duration_ongoing");
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const days = Math.round(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (days <= 1) return t("medication_factsheet.duration_1_day");
  if (days < 7) {
    return t("medication_factsheet.duration_n_days", {
      params: { n: String(days) },
    });
  }
  if (days === 7) return t("medication_factsheet.duration_1_week");
  if (days < 30) {
    return t("medication_factsheet.duration_n_weeks", {
      params: { n: String(Math.round(days / 7)) },
    });
  }
  if (days < 60) return t("medication_factsheet.duration_1_month");
  return t("medication_factsheet.duration_n_months", {
    params: { n: String(Math.round(days / 30)) },
  });
}

function formatSupplementRoute(
  route: string,
  t: (key: string, opts?: { params?: Record<string, string> }) => string,
): string {
  const key = `medication_factsheet.route_${route}`;
  const translated = t(key);
  return translated === key ? route : translated;
}

function normalizeSupplementName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function linkedBlockNames(block: ContentBlock): string[] {
  const names = [block.title];
  if (block.kind === "medication") {
    const medicationBlock = block as ContentBlock<"medication">;
    names.push(
      medicationBlock.body.genericName,
      ...(medicationBlock.body.aliases ?? []),
    );
  }
  if (block.kind === "supplement") {
    const supplementBlock = block as ContentBlock<"supplement">;
    names.push(supplementBlock.body.name);
  }
  return names.map(normalizeSupplementName).filter(Boolean);
}

function isLinkedBlockInMedicationList(
  block: ContentBlock,
  medications: Medication[],
) {
  const blockNames = linkedBlockNames(block);
  return medications.some((medication) => {
    const medicationNames = [
      medication.name,
      medication.molecule,
      medication.moleculeType,
    ]
      .filter((value): value is string => Boolean(value))
      .map(normalizeSupplementName);
    return medicationNames.some((medicationName) =>
      blockNames.some(
        (blockName) =>
          medicationName === blockName ||
          medicationName.includes(blockName) ||
          blockName.includes(medicationName),
      ),
    );
  });
}

function useUserSupplementContext(title: string, bodyName: string) {
  let logsData: MedicationLog[] | undefined;
  let medsData: Medication[] | undefined;

  try {
    const logsQuery = useMedicationLogs();
    logsData = logsQuery.data;
  } catch {
    // No auth context — skip
  }

  try {
    const medsQuery = useMedications();
    medsData = medsQuery.data;
  } catch {
    // No auth context — skip
  }

  const logs = useMemo(() => logsData ?? [], [logsData]);
  const medications = useMemo(() => medsData ?? [], [medsData]);
  const names = useMemo(
    () => [title, bodyName].map(normalizeSupplementName).filter(Boolean),
    [title, bodyName],
  );

  const matchingLogs = useMemo(() => {
    if (logs.length === 0) return [];
    return logs.filter((log) => {
      const logName = normalizeSupplementName(log.medicationName);
      return names.some(
        (name) => logName.includes(name) || name.includes(logName),
      );
    });
  }, [logs, names]);

  const currentSupplement = useMemo(() => {
    if (medications.length === 0) return null;
    return (
      medications.find((medication) => {
        const medNames = [
          medication.name,
          medication.molecule,
          medication.moleculeType,
        ]
          .filter((value): value is string => Boolean(value))
          .map(normalizeSupplementName);
        return medNames.some((medName) =>
          names.some((name) => medName === name || medName.includes(name)),
        );
      }) ?? null
    );
  }, [medications, names]);

  return { matchingLogs, currentSupplement, medications };
}

function UserSupplementCard({
  label,
  displayName,
  userContext,
  currentSupplement,
  matchingLogs,
}: {
  label: string;
  displayName: string;
  userContext: MedicationUserContext | undefined;
  currentSupplement: Medication | null;
  matchingLogs: MedicationLog[];
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { format: fmtDate } = useDateFormat();
  const dosage =
    userContext?.dosage ??
    (currentSupplement?.dose != null
      ? `${currentSupplement.dose}${currentSupplement.doseUnit ? ` ${currentSupplement.doseUnit}` : ""}`
      : null) ??
    currentSupplement?.notes ??
    null;
  const route = userContext?.route
    ? formatSupplementRoute(userContext.route, t)
    : currentSupplement?.intakeType
      ? formatSupplementRoute(currentSupplement.intakeType, t)
      : null;
  const schedule =
    userContext?.timeTaken && userContext.timeTaken.length > 0
      ? formatSupplementSchedule(userContext.timeTaken, t)
      : null;
  const startDate = userContext?.startDate
    ? formatSupplementDateCompact(userContext.startDate, locale)
    : null;
  const endDate = userContext?.endDate
    ? formatSupplementDateCompact(userContext.endDate, locale)
    : null;
  const duration = userContext?.startDate
    ? formatSupplementDuration(userContext.startDate, userContext.endDate, t)
    : null;
  const since =
    userContext?.sinceDay ??
    (currentSupplement?.createdAt
      ? fmtDate(currentSupplement.createdAt)
      : null);
  const notes = userContext?.notes ?? null;
  const isStoppedContext = isSupplementStopped(notes);
  const isPastContext = hasPastSupplementEndDate(userContext?.endDate);
  const contextLabel = isStoppedContext
    ? t("learn.supplement_detail.you_stopped_taking")
    : isPastContext
      ? t("learn.supplement_detail.you_have_taken")
      : label;
  const savedSupplementName =
    userContext?.originalName ?? currentSupplement?.name;
  const patientSupplementName =
    savedSupplementName &&
    normalizeSupplementName(savedSupplementName) !==
      normalizeSupplementName(displayName)
      ? savedSupplementName
      : null;
  const dateMeta =
    isStoppedContext && endDate
      ? t("medication_factsheet.context_stopped_on", {
          params: { date: endDate },
        })
      : isPastContext && endDate
        ? t("medication_factsheet.context_ended_on", {
            params: { date: endDate },
          })
        : startDate
          ? t("medication_factsheet.context_since", {
              params: { date: startDate },
            })
          : since
            ? t("medication_factsheet.context_since", {
                params: { date: since },
              })
            : null;

  const rows: FactSheetContextRow[] = [
    patientSupplementName
      ? {
          label: t("learn.supplement_detail.row_your_supplement"),
          value: patientSupplementName,
        }
      : null,
    dosage
      ? { label: t("medication_factsheet.row_dosage"), value: dosage }
      : null,
    route ? { label: t("medication_factsheet.row_route"), value: route } : null,
    schedule
      ? { label: t("medication_factsheet.row_schedule"), value: schedule }
      : null,
    startDate && (isStoppedContext || isPastContext)
      ? { label: t("medication_factsheet.row_start_date"), value: startDate }
      : null,
    endDate && !isStoppedContext && !isPastContext
      ? { label: t("medication_factsheet.row_end_date"), value: endDate }
      : null,
    duration
      ? { label: t("medication_factsheet.row_duration"), value: duration }
      : null,
    notes ? { label: t("medication_factsheet.row_notes"), value: notes } : null,
    matchingLogs.length > 0
      ? {
          label: t("medication_factsheet.row_doses_logged"),
          value: `${matchingLogs.length}${
            matchingLogs[0] ? ` \u2022 ${fmtDate(matchingLogs[0].takenAt)}` : ""
          }`,
        }
      : null,
  ].filter((row): row is FactSheetContextRow => row !== null);

  return (
    <FactSheetContextCard
      label={contextLabel}
      meta={dateMeta}
      rows={rows}
      expandLabel={t("medication_factsheet.context_show_details")}
      collapseLabel={t("medication_factsheet.context_hide_details")}
    />
  );
}

export function SupplementDetailContent({
  item,
  onClose,
  skipExitAnimation,
  userContext,
}: {
  item: ContentBlock<"supplement">;
  onClose: () => void;
  skipExitAnimation?: boolean;
  userContext?: MedicationUserContext;
}) {
  const t = useTranslations();
  const capturePressContent = usePressCapture("SupplementDetailContent");
  const insets = useSafeAreaInsets();
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const [evidenceModal, setEvidenceModal] = useState<string | null>(null);
  const [foodModal, setFoodModal] = useState<string | null>(null);
  const onScrollHaptics = useScrollHaptics();
  const { matchingLogs, currentSupplement, medications } =
    useUserSupplementContext(item.title, item.body.name);
  const [linkedItem, setLinkedItem] = useState<
    ContentBlock<"medication"> | ContentBlock<"supplement"> | null
  >(null);
  const takingLinkedBlockIds = useMemo(
    () =>
      (item.body.interactions ?? [])
        .map((interaction) => interaction.linkedBlockId)
        .filter((id): id is string => Boolean(id))
        .filter((id) => {
          const linkedBlock = getBlock(id as ContentBlockId);
          return linkedBlock
            ? isLinkedBlockInMedicationList(linkedBlock, medications)
            : false;
        }),
    [item.body.interactions, medications],
  );
  const handleLinkedBlockPress = useCallback((blockId: string) => {
    const linkedBlock = getBlock(blockId as ContentBlockId);
    if (
      linkedBlock?.kind === "medication" ||
      linkedBlock?.kind === "supplement"
    ) {
      setLinkedItem(
        linkedBlock as ContentBlock<"medication"> | ContentBlock<"supplement">,
      );
    }
  }, []);

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScrollHaptics(e);
    },
    [onScrollHaptics],
  );

  const { body } = item;
  const cycleNote = getSupplementCycleNote(body.timing);
  const headerTags: FactSheetTagItem[] = [
    {
      kind: "category",
      label: t("learn.supplement_detail.is_a_supplement"),
      icon: Leaf,
    },
    {
      kind: "evidence",
      label: t("learn.supplement_detail.strength_of_evidence", {
        params: {
          strength: t(
            `learn.supplement_detail.evidence_strength.${body.evidenceStrength}`,
          ),
        },
      }),
      strength: body.evidenceStrength,
    },
    { kind: "audience", audience: body.forWhom },
  ];

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <View
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* Close */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onPress={handleClose}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor={semanticColors.surface.muted}
                  pressedBackgroundColor={semanticColors.surface.muted}
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              <FactSheetHeader
                title={body.name}
                imageSource={SUPPLEMENT_BOTTLES[bottleIndex(item.id as string)]}
                imageSize={92}
                tags={headerTags}
              />

              {userContext || currentSupplement ? (
                <UserSupplementCard
                  label={t("learn.supplement_detail.you_are_taking")}
                  displayName={body.name}
                  userContext={userContext}
                  currentSupplement={currentSupplement}
                  matchingLogs={matchingLogs}
                />
              ) : null}

              {!userContext && !currentSupplement && matchingLogs.length > 0 ? (
                <UserSupplementCard
                  label={t("learn.supplement_detail.you_have_taken")}
                  displayName={body.name}
                  userContext={undefined}
                  currentSupplement={null}
                  matchingLogs={matchingLogs}
                />
              ) : null}

              {/* How to take */}
              <View style={{ marginTop: 24 }}>
                <DetailSectionTitle icon={Clock}>
                  {t("learn.supplement_detail.section_how_to_take")}
                </DetailSectionTitle>
                <View style={{ gap: 10 }}>
                  {body.dailyTiming ? (
                    <View style={{ flexDirection: "row", gap: 10 }}>
                      <SupplementFactRow
                        icon={Pill}
                        label={t(
                          "learn.supplement_detail.how_to_take_label_dosage",
                        )}
                        value={body.dosageRange}
                        flex={0.32}
                      />
                      <SupplementFactRow
                        icon={Clock}
                        label={t(
                          "learn.supplement_detail.how_to_take_label_best_time",
                        )}
                        value={body.dailyTiming}
                        flex={0.68}
                      />
                    </View>
                  ) : (
                    <SupplementFactRow
                      icon={Pill}
                      label={t(
                        "learn.supplement_detail.how_to_take_label_dosage",
                      )}
                      value={body.dosageRange}
                    />
                  )}
                  {cycleNote ? (
                    <SupplementFactRow
                      icon={CalendarClock}
                      label={t(
                        "learn.supplement_detail.how_to_take_label_cycle_note",
                      )}
                      value={cycleNote}
                    />
                  ) : null}
                </View>
              </View>

              {/* What to expect */}
              {body.expectations && (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={Lightbulb}>
                    {t("learn.supplement_detail.section_what_to_expect")}
                  </DetailSectionTitle>
                  <SupplementExpectationRows
                    items={body.expectations.canExpect}
                    tone="positive"
                  />
                </View>
              )}

              {/* What not to expect */}
              {body.expectations?.shouldNotExpect?.length ? (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={AlertCircle}>
                    {t("learn.supplement_detail.section_keep_in_mind")}
                  </DetailSectionTitle>
                  <SupplementExpectationRows
                    items={body.expectations.shouldNotExpect}
                    tone="caution"
                  />
                </View>
              ) : null}

              {/* Bioavailability */}
              {body.bioavailability ? (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={FlaskConical}>
                    {t("learn.supplement_detail.section_bioavailability")}
                  </DetailSectionTitle>
                  <BioavailabilityRows
                    bioavailability={body.bioavailability}
                    t={t}
                  />
                </View>
              ) : null}

              {/* Interactions with other meds / supplements */}
              {body.interactions?.length ? (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={AlertTriangle}>
                    {t("learn.supplement_detail.section_interacts_with")}
                  </DetailSectionTitle>
                  <InteractionsList
                    interactions={body.interactions}
                    onLinkedBlockPress={handleLinkedBlockPress}
                    takingLinkedBlockIds={takingLinkedBlockIds}
                    takingLabel={t(
                      "learn.supplement_detail.interaction_taking",
                    )}
                    severityLabels={{
                      note: t(
                        "learn.supplement_detail.interaction_severity_note",
                      ),
                      caution: t(
                        "learn.supplement_detail.interaction_severity_caution",
                      ),
                      critical: t(
                        "learn.supplement_detail.interaction_severity_critical",
                      ),
                    }}
                  />
                </View>
              ) : null}

              {/* Food sources */}
              {body.foods?.length ? (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={UtensilsCrossed}>
                    {t("learn.supplement_detail.section_food_sources")}
                  </DetailSectionTitle>
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 6,
                    }}
                  >
                    {body.foods.map((food) => (
                      <Pressable
                        key={food}
                        onPress={() => {
                          capturePressContent(
                            pressLabel(getFoodLabel(t, food)),
                            "open_food_detail",
                          );
                          setFoodModal(food);
                        }}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 6,
                          paddingHorizontal: 12,
                          paddingVertical: 7,
                          borderRadius: 8,
                          backgroundColor: semanticColors.surface.muted,
                          borderWidth: 1,
                          borderColor: semanticColors.border.subtle,
                        }}
                      >
                        <Text style={{ fontSize: 12 }}>
                          {FOOD_ICONS[food] ?? "🍽"}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: "500",
                            color: EMBER.colors.textMid,
                            letterSpacing: 0.2,
                          }}
                        >
                          {getFoodLabel(t, food)}
                        </Text>
                        <Info
                          size={12}
                          color={EMBER.colors.textFaint}
                          strokeWidth={1.5}
                        />
                      </Pressable>
                    ))}
                  </View>
                </View>
              ) : null}

              {/* Evidence */}
              {item.sources?.length ? (
                <View style={{ marginTop: 24 }}>
                  <DetailSectionTitle icon={BookOpen}>
                    {t("learn.supplement_detail.section_evidence")}
                  </DetailSectionTitle>
                  <EvidenceSourceRows
                    sources={item.sources}
                    onEvidenceLevelPress={setEvidenceModal}
                  />
                </View>
              ) : null}

              <View style={{ marginTop: 24 }}>
                <InterestButton
                  featureId={item.id}
                  featureType="supplement"
                  featureName={body.name}
                  label={t("learn.supplement_detail.interest_button_label")}
                  capturedLabel={t(
                    "learn.supplement_detail.interest_button_captured",
                  )}
                />
                <SupplementOrderingNote />
              </View>
            </View>
          </Animated.ScrollView>
        </View>
      </Animated.View>

      <EvidenceLevelModal
        level={evidenceModal}
        visible={evidenceModal !== null}
        onClose={() => setEvidenceModal(null)}
      />

      <FoodDetailModal
        food={foodModal}
        visible={foodModal !== null}
        onClose={() => setFoodModal(null)}
      />

      <LinkedContentSheet
        item={linkedItem}
        onClose={() => setLinkedItem(null)}
      />
    </View>
  );
}

export default function SupplementDetailSheet({
  item,
  onClose,
  userContext,
}: Props) {
  if (!item) return null;
  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      <SupplementDetailContent
        item={item}
        onClose={onClose}
        userContext={userContext}
      />
    </Modal>
  );
}
